import type { LegacyQuoteCard } from '@/types';

export function getReceiptCode(cardId: string): string {
  let hash = 2166136261;

  for (let index = 0; index < cardId.length; index += 1) {
    hash ^= cardId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `RE-${(hash >>> 0).toString(36).toUpperCase().padStart(6, '0').slice(0, 6)}`;
}

export function buildReceiptPath(receiptId: string): string {
  return `/r/${encodeURIComponent(receiptId)}`;
}

export function buildReceiptUrl(receiptId: string): string {
  const path = buildReceiptPath(receiptId);
  return typeof window === 'undefined' ? path : new URL(path, window.location.origin).toString();
}

export function buildLegacyReceiptPath(card: LegacyQuoteCard, searchQuery?: string): string {
  const params = new URLSearchParams();
  if (searchQuery?.trim()) params.set('q', searchQuery.trim());

  const query = params.toString();
  return `/r/${encodeURIComponent(card.id)}${query ? `?${query}` : ''}`;
}

export function buildLegacyReceiptUrl(card: LegacyQuoteCard, searchQuery?: string): string {
  const path = buildLegacyReceiptPath(card, searchQuery);
  return typeof window === 'undefined' ? path : new URL(path, window.location.origin).toString();
}

export function getReceiptFingerprintPayload(card: LegacyQuoteCard, searchQuery?: string): string {
  return JSON.stringify({
    version: 1,
    searchQuery: searchQuery?.trim() ?? '',
    card: {
      id: card.id,
      quote: card.quote,
      source: card.source,
      documentTitle: card.documentTitle,
      date: card.date,
      canonicalLink: card.canonicalLink,
      lane: card.lane,
      laneLabel: card.laneLabel,
      context: card.context ?? null,
      effectiveDate: card.effectiveDate ?? null,
      amendedBy: card.amendedBy ?? null,
      supersededBy: card.supersededBy ?? null,
      stance: card.stance ?? null,
    },
  });
}

export async function getReceiptFingerprint(
  card: LegacyQuoteCard,
  searchQuery?: string
): Promise<string> {
  const bytes = new TextEncoder().encode(getReceiptFingerprintPayload(card, searchQuery));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
